/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseAdjustmentsPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/adjustments メンバー決策（spec §8.2）—
 *                落札単価 < 希望/最低単価時 逐品三選一：
 *                希望調整（price_adjust）/ 返品（return）/ 預り（hold）。
 *  Author      : EF Engineering
 *  Created     : 2026-06-01
 *  Updated     : 2026-06-01
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端：v13_member_decisions（POST /cases/:id/adjustments，Idempotency-Key）。
 *  RBAC：create/cancel = marketplace.member_decisions.create
 *        （PERMISSIONS.adjustmentPriceAdjust）。
 *  業務不變式（business-rules §4 / 工程規格書 §6.3）：
 *    希望調整 J − M ∈ [5,20] 円（J = 落札単価, M = メンバー調整後単価）。
 *    前端做完整 [5,20] 校驗（react-hook-form + zod），後端為最終權威（僅硬性
 *    強制下限 J − M ≥ 5 → PriceAdjustTooHigh）。
 *  返品 / 預り 僅記錄不結算（Q5/Q6 未 sign-off）；location/deadline 併入 reason 送出。
 *  ⚠️ 単価一律字串型 Decimal，前端不得轉 float（CLAUDE.md §2）。
 */

import { useMemo, type ReactElement } from 'react'
import { useForm, useWatch, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase, type CaseItemDto } from '../api/cases'
import { listAwards, type AwardDto } from '../api/auction'
import {
  cancelMemberDecision,
  listMemberDecisions,
  recordMemberDecision,
  type DecisionType,
  type MemberDecisionDto,
} from '../api/adjustments'
import { ApiError } from '../api/client'
import { PRICE_ADJUST_GAP_MAX, PRICE_ADJUST_GAP_MIN, gapInRange } from '../lib/settlementShare'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** 希望調整下限/上限（business-rules §4：J − M ∈ [5,20]，來自 lib/settlementShare）。 */
const GAP_MIN = PRICE_ADJUST_GAP_MIN
const GAP_MAX = PRICE_ADJUST_GAP_MAX

/** 千分位（顯示用，不改原始 Decimal 字串）。 */
function fmtAmount(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  return Number.isFinite(num) ? num.toLocaleString('ja-JP', { maximumFractionDigits: 2 }) : value
}

function fmtTime(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString('ja-JP')
}

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

/** 需決策品項（落札単価 < 希望/最低単価）。 */
interface DecidableItem {
  item: CaseItemDto
  /** 希望/最低単価（item.min_unit_price 字串）。 */
  minPrice: string
  /** 落札単価 J（award.auction_unit_price 字串）。 */
  auctionPrice: string
}

export function CaseAdjustmentsPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canDecide = can(PERMISSIONS.adjustmentPriceAdjust)

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })
  const awardsQuery = useQuery({
    queryKey: ['case', caseId, 'awards'],
    queryFn: () => listAwards(caseId),
    enabled: caseId !== '',
  })
  const decisionsQuery = useQuery({
    queryKey: ['case', caseId, 'member-decisions'],
    queryFn: () => listMemberDecisions(caseId),
    enabled: caseId !== '',
  })

  const items = useMemo<CaseItemDto[]>(() => caseQuery.data?.items ?? [], [caseQuery.data])
  const awards = useMemo<AwardDto[]>(() => awardsQuery.data ?? [], [awardsQuery.data])

  /** item_id → 落札単価（confirmed award）。 */
  const awardByItem = useMemo(() => {
    const map = new Map<number, string>()
    for (const a of awards) map.set(a.item_id, a.auction_unit_price)
    return map
  }, [awards])

  /**
   * 需決策品項：有 confirmed 落札単価 且 落札単価 < 希望/最低単価。
   * 落札単価 ≥ 希望/最低単価者毋須決策（後端 need_decision_items 同義）。
   */
  const decidable = useMemo<DecidableItem[]>(() => {
    const out: DecidableItem[] = []
    for (const item of items) {
      const minPrice = item.min_unit_price
      const auctionPrice = awardByItem.get(item.id)
      if (minPrice === null || minPrice === undefined || auctionPrice === undefined) continue
      if (Number(auctionPrice) < Number(minPrice)) {
        out.push({ item, minPrice, auctionPrice })
      }
    }
    return out
  }, [items, awardByItem])

  const isLoading = caseQuery.isPending || awardsQuery.isPending || decisionsQuery.isPending

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }
  if (caseQuery.isError || !caseQuery.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.detail.loadError')}
        {errDetail(caseQuery.error)}
      </p>
    )
  }

  const decisions = decisionsQuery.data ?? []
  const caseNo = caseQuery.data.case_no

  const itemLabel = (id: number): string => {
    const it = items.find((x) => x.id === id)
    return it ? `#${it.line_no} ${it.item_name}` : `#${id}`
  }

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.adjustments')}</span>
        <h2 className="text-lg font-semibold">{t('adjust.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('adjust.subtitle')}</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('adjust.form.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">{t('adjust.gateHint')}</p>
          {!canDecide ? (
            <Badge variant="outline">{t('rbac.forbidden')}</Badge>
          ) : decidable.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('p2.items.empty')}</p>
          ) : (
            <DecisionForm
              t={t}
              caseNo={caseNo}
              decidable={decidable}
              existing={decisions}
              onSubmit={(body) => recordMemberDecision(caseId, body)}
              onSuccess={() => {
                void queryClient.invalidateQueries({
                  queryKey: ['case', caseId, 'member-decisions'],
                })
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* 需決策品項一覧（落札単価 < 希望/最低単価）*/}
      <Card>
        <CardHeader>
          <CardTitle>{t('adjust.needsDecision')}</CardTitle>
        </CardHeader>
        <CardContent>
          {decidable.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('p2.items.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adjust.item')}</TableHead>
                    <TableHead className="text-right">{t('adjust.minPrice')}</TableHead>
                    <TableHead className="text-right">{t('adjust.auctionPrice')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decidable.map((d) => (
                    <TableRow key={d.item.id} data-testid={`decision-needed-row-${d.item.id}`}>
                      <TableCell className="font-medium">
                        #{d.item.line_no} {d.item.item_name}
                      </TableCell>
                      <TableCell className="text-right">¥{fmtAmount(d.minPrice)}</TableCell>
                      <TableCell className="text-right">¥{fmtAmount(d.auctionPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 已記錄決策 */}
      <Card>
        <CardHeader>
          <CardTitle>{t('adjust.list.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {decisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('adjust.list.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('adjust.item')}</TableHead>
                    <TableHead>{t('adjust.decisionType')}</TableHead>
                    <TableHead className="text-right">{t('adjust.minPrice')}</TableHead>
                    <TableHead className="text-right">{t('adjust.auctionPrice')}</TableHead>
                    <TableHead className="text-right">{t('adjust.adjustedPrice')}</TableHead>
                    <TableHead>{t('adjust.status')}</TableHead>
                    <TableHead>{t('adjust.decidedAt')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decisions.map((d) => (
                    <DecisionRow
                      key={d.id}
                      t={t}
                      decision={d}
                      label={itemLabel(d.item_id)}
                      canCancel={canDecide}
                      onCancelled={() =>
                        void queryClient.invalidateQueries({
                          queryKey: ['case', caseId, 'member-decisions'],
                        })
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---- 決策表單（react-hook-form + zod）-------------------------------------

const DECISION_TYPES: { value: DecisionType; labelKey: string }[] = [
  { value: 'price_adjust', labelKey: 'adjust.type.priceAdjust' },
  { value: 'return', labelKey: 'adjust.type.return' },
  { value: 'hold', labelKey: 'adjust.type.hold' },
]

function DecisionForm({
  t,
  caseNo,
  decidable,
  existing,
  onSubmit,
  onSuccess,
}: {
  t: (key: string) => string
  caseNo: string
  decidable: DecidableItem[]
  existing: MemberDecisionDto[]
  onSubmit: (body: {
    item_id: number
    decision_type: string
    adjusted_member_unit_price?: string | null
    reason?: string | null
  }) => Promise<MemberDecisionDto>
  onSuccess: () => void
}): ReactElement {
  // 已有 decided 決策的品項不再可選（後端 MemberDecisionAlreadyRecorded）。
  const decidedItemIds = useMemo(() => {
    const s = new Set<number>()
    for (const d of existing) if (d.status === 'decided') s.add(d.item_id)
    return s
  }, [existing])

  const openItems = useMemo(
    () => decidable.filter((d) => !decidedItemIds.has(d.item.id)),
    [decidable, decidedItemIds],
  )

  /** zod schema：依 decision_type 條件式驗證 J − M ∈ [5,20]。 */
  const schema = useMemo(
    () =>
      z
        .object({
          itemId: z.string().min(1),
          decisionType: z.enum(['price_adjust', 'return', 'hold']),
          adjustedPrice: z.string().trim().optional().default(''),
          reason: z.string().trim().optional().default(''),
          holdLocation: z.string().trim().optional().default(''),
          holdDeadline: z.string().trim().optional().default(''),
        })
        .superRefine((val, ctx) => {
          if (val.decisionType !== 'price_adjust') return
          const adj = val.adjustedPrice.trim()
          if (adj === '' || !Number.isFinite(Number(adj))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['adjustedPrice'],
              message: t('adjust.errAdjustedRequired'),
            })
            return
          }
          const target = openItems.find((o) => String(o.item.id) === val.itemId)
          if (!target) return
          // J − M ∈ [5,20]（J = 落札単価, M = 調整後単価）。
          if (!gapInRange(Number(target.auctionPrice), Number(adj))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['adjustedPrice'],
              message: t('adjust.errGapRange'),
            })
          }
        }),
    [t, openItems],
  )

  type FormValues = z.input<typeof schema>

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      itemId: '',
      decisionType: 'price_adjust',
      adjustedPrice: '',
      reason: '',
      holdLocation: '',
      holdDeadline: '',
    },
  })

  const decisionType = useWatch({ control: form.control, name: 'decisionType' })
  const itemId = useWatch({ control: form.control, name: 'itemId' })
  const adjustedPrice = useWatch({ control: form.control, name: 'adjustedPrice' })

  const selected = useMemo(
    () => openItems.find((o) => String(o.item.id) === itemId),
    [openItems, itemId],
  )

  // 即時顯示 J − M 差額（僅供使用者參考；正式校驗在 zod）。
  const gapPreview = useMemo(() => {
    const adj = (adjustedPrice ?? '').trim()
    if (!selected || adj === '') return null
    const m = Number(adj)
    if (!Number.isFinite(m)) return null
    return Number(selected.auctionPrice) - m
  }, [selected, adjustedPrice])

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const decision_type = values.decisionType
      let reason: string | null = values.reason?.trim() || null
      let adjusted_member_unit_price: string | null = null
      if (decision_type === 'price_adjust') {
        adjusted_member_unit_price = (values.adjustedPrice ?? '').trim()
      } else if (decision_type === 'hold') {
        // 預り：location / deadline 併入 reason（後端 DTO 僅有 reason 欄）。
        const parts = [
          values.holdLocation?.trim() ? `${t('adjust.holdLocation')}: ${values.holdLocation.trim()}` : '',
          values.holdDeadline?.trim() ? `${t('adjust.holdDeadline')}: ${values.holdDeadline.trim()}` : '',
          values.reason?.trim() ?? '',
        ].filter(Boolean)
        reason = parts.length > 0 ? parts.join(' / ') : null
      }
      return onSubmit({
        item_id: Number(values.itemId),
        decision_type,
        adjusted_member_unit_price,
        reason,
      })
    },
    onSuccess: () => {
      toast.success(t('adjust.success'))
      form.reset({
        itemId: '',
        decisionType: 'price_adjust',
        adjustedPrice: '',
        reason: '',
        holdLocation: '',
        holdDeadline: '',
      })
      onSuccess()
    },
    onError: (error) => toast.error(`${t('adjust.error')}${errDetail(error)}`),
  })

  const submit = form.handleSubmit((values) => mutation.mutate(values))

  if (openItems.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('adjust.list.empty')}</p>
  }

  return (
    <form className="space-y-4" onSubmit={(e) => void submit(e)}>
      <input type="hidden" value={caseNo} readOnly />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* 品項 */}
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('adjust.item')}</span>
          <Controller
            control={form.control}
            name="itemId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  data-testid="decision-item-select"
                  aria-invalid={!!form.formState.errors.itemId}
                >
                  <SelectValue placeholder={t('adjust.selectItem')} />
                </SelectTrigger>
                <SelectContent>
                  {openItems.map((o) => (
                    <SelectItem
                      key={o.item.id}
                      value={String(o.item.id)}
                      data-testid={`decision-item-option-${o.item.id}`}
                    >
                      #{o.item.line_no} {o.item.item_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </label>

        {/* 決策種別 */}
        <label className="space-y-1 text-sm">
          <span className="text-muted-foreground">{t('adjust.decisionType')}</span>
          <Controller
            control={form.control}
            name="decisionType"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DECISION_TYPES.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {t(d.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </label>
      </div>

      {/* 參考：選定品項的 J / 希望価 */}
      {selected ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label={t('adjust.minPrice')} value={`¥${fmtAmount(selected.minPrice)}`} />
          <Stat label={t('adjust.auctionPrice')} value={`¥${fmtAmount(selected.auctionPrice)}`} />
          {decisionType === 'price_adjust' ? (
            <Stat
              label={t('adjust.gap')}
              value={gapPreview === null ? '—' : `${gapPreview}`}
              warn={gapPreview !== null && (gapPreview < GAP_MIN || gapPreview > GAP_MAX)}
            />
          ) : null}
        </div>
      ) : null}

      {/* 希望調整：調整後単価 */}
      {decisionType === 'price_adjust' ? (
        <div className="space-y-1">
          <label className="text-sm text-muted-foreground" htmlFor="adjustedPrice">
            {t('adjust.adjustedPrice')}
          </label>
          <Input
            id="adjustedPrice"
            inputMode="decimal"
            data-testid="decision-adjusted-price"
            aria-invalid={!!form.formState.errors.adjustedPrice}
            placeholder={t('adjust.adjustedPrice')}
            {...form.register('adjustedPrice')}
          />
          <p className="text-xs text-muted-foreground">{t('adjust.adjustedPriceHint')}</p>
          {form.formState.errors.adjustedPrice ? (
            <p className="text-xs text-destructive">
              {form.formState.errors.adjustedPrice.message}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* 預り：場所 + 期限 */}
      {decisionType === 'hold' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="holdLocation">
              {t('adjust.holdLocation')}
            </label>
            <Input
              id="holdLocation"
              placeholder={t('adjust.holdLocationPlaceholder')}
              {...form.register('holdLocation')}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground" htmlFor="holdDeadline">
              {t('adjust.holdDeadline')}
            </label>
            <Input
              id="holdDeadline"
              placeholder={t('adjust.holdDeadlinePlaceholder')}
              {...form.register('holdDeadline')}
            />
          </div>
        </div>
      ) : null}

      {/* 理由（共用；返品必填語意由業務面，後端可選）*/}
      <div className="space-y-1">
        <label className="text-sm text-muted-foreground" htmlFor="reason">
          {t('adjust.reason')}
        </label>
        <Input
          id="reason"
          placeholder={t('adjust.reasonPlaceholder')}
          {...form.register('reason')}
        />
      </div>

      <Button type="submit" data-testid="decision-submit" disabled={mutation.isPending}>
        {mutation.isPending ? t('adjust.submitting') : t('adjust.submit')}
      </Button>
    </form>
  )
}

// ---- 已記錄決策列（含取消）-------------------------------------------------

function DecisionRow({
  t,
  decision,
  label,
  canCancel,
  onCancelled,
}: {
  t: (key: string) => string
  decision: MemberDecisionDto
  label: string
  canCancel: boolean
  onCancelled: () => void
}): ReactElement {
  const mutation = useMutation({
    mutationFn: () => cancelMemberDecision(decision.id),
    onSuccess: () => {
      toast.success(t('adjust.cancelSuccess'))
      onCancelled()
    },
    onError: (error) => toast.error(`${t('adjust.cancelError')}${errDetail(error)}`),
  })

  const typeLabel: Record<string, string> = {
    price_adjust: t('adjust.type.priceAdjust'),
    return: t('adjust.type.return'),
    hold: t('adjust.type.hold'),
  }

  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell>{typeLabel[decision.decision_type] ?? decision.decision_type}</TableCell>
      <TableCell className="text-right">¥{fmtAmount(decision.original_member_unit_price)}</TableCell>
      <TableCell className="text-right">¥{fmtAmount(decision.auction_unit_price)}</TableCell>
      <TableCell className="text-right">
        {decision.adjusted_member_unit_price
          ? `¥${fmtAmount(decision.adjusted_member_unit_price)}`
          : '—'}
      </TableCell>
      <TableCell>
        <Badge variant={decision.status === 'decided' ? 'secondary' : 'outline'}>
          {decision.status}
        </Badge>
      </TableCell>
      <TableCell>{fmtTime(decision.decided_at)}</TableCell>
      <TableCell className="text-right">
        {canCancel && decision.status === 'decided' ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t('adjust.cancelling') : t('adjust.cancel')}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

function Stat({
  label,
  value,
  warn,
}: {
  label: string
  value: string
  warn?: boolean
}): ReactElement {
  return (
    <div className={`rounded-md border p-3 ${warn ? 'border-destructive' : 'border-border'}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium ${warn ? 'text-destructive' : ''}`}>{value}</dd>
    </div>
  )
}
